/**
 * Sélecteur d'emplacement réutilisable (popover ancré au point de clic).
 *
 * Un seul composant `LocationPicker` + un hook `useLocationPicker` pilotent les DEUX points
 * d'entrée de la fiche bouteille (case du tableau millésime×format et lien de la colonne de
 * droite). Le rangement d'un millésime/format s'applique en un clic, sans bouton « Valider ».
 *
 * Données : espaces de « Paramètres › Décrivez votre cave » (GET /cave/locations).
 * Mutations : POST /cave/references/{id}/locations (+ undo) via la file hors-ligne (services).
 */
import { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { X, ChevronDown } from 'lucide-react';
import { caveApi } from '../../services/api';
import { placeLocations } from '../../offline/store';

const SEP = '\u00b7';

/** Normalise un code/nom pour la recherche tolérante (sans accents, espaces ni séparateurs). */
const norm = (s) => (s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]/g, '');

/** Réduit une liste résolue au format de stockage {space_id, emp, quantite}. */
const toStore = (locs) => (locs || []).map((l) => ({ space_id: l.space_id, emp: l.emp || null, quantite: l.quantite }));

/** Résout des rangements {space_id, emp, quantite} en {code, label, precise} à partir des espaces. */
export function resolveLocations(locations, spaces) {
  const byId = Object.fromEntries((spaces || []).map((s) => [s.id, s]));
  return (locations || []).map((pl) => {
    const s = byId[pl.space_id];
    if (!s) return { ...pl, code: pl.code || '?', label: pl.label || '?', precise: false };
    if (pl.emp && !s.single) {
      const e = s.emplacements.find((x) => x.key === pl.emp);
      if (e) return { space_id: s.id, emp: pl.emp, quantite: pl.quantite, code: e.code, label: `${e.code} \u2013 ${s.name}`, precise: true, single: false };
    }
    return { space_id: s.id, emp: null, quantite: pl.quantite, code: s.short_code, label: `${s.short_code} \u2013 ${s.name}`, precise: !!s.single, single: !!s.single };
  });
}

const freeLabel = (free) => (free > 0 ? `${free} libres` : free === 0 ? 'plein' : `+${-free} en trop`);
const freeClass = (free) => (free < 0 ? 'text-red-700' : free === 0 ? 'text-n500' : 'text-n600');

/**
 * Hook de pilotage du sélecteur : état d'ouverture (ancre + référence visée) + logique de
 * rangement partagée (placement en un clic, notification Annuler 6 s, mise à jour du cache).
 */
export function useLocationPicker({ qc, cuveeId, spaces }) {
  const [state, setState] = useState(null); // { ref, vintage, anchorRect, align }

  const openAt = useCallback((el, ref, vintage, align = 'left') => {
    const r = el.getBoundingClientRect();
    setState({ ref, vintage, align, anchorRect: { top: r.top, bottom: r.bottom, left: r.left, right: r.right, width: r.width } });
  }, []);
  const close = useCallback(() => setState(null), []);

  // Applique un nouveau rangement à une référence (optimiste + serveur + toast Annuler).
  const place = useCallback(async (ref, nextStore, { keepOpen, overflow } = {}) => {
    const prevStore = toStore(ref.locations);
    const resolved = resolveLocations(nextStore, spaces);
    const primary = resolved[0];
    // Mise à jour optimiste du cache (fonctionne aussi hors-ligne).
    qc.setQueryData(['cuvee', cuveeId], (d) => d && ({
      ...d, references: d.references.map((r) => r.id === ref.id
        ? { ...r, locations: resolved, location_code: primary?.code || '', location_label: primary?.label || null } : r),
    }));
    if (!keepOpen) setState(null);
    try {
      const res = await placeLocations(ref.id, { locations: nextStore, to_label: primary?.label || '' });
      if (res.reference) {
        qc.setQueryData(['cuvee', cuveeId], (d) => d && ({ ...d, references: d.references.map((r) => r.id === ref.id ? { ...r, ...res.reference, format_label: res.reference.format_label || r.format_label } : r) }));
      }
      const over = (res.overflow || overflow || []);
      const moved = resolved.reduce((s, l) => s + l.quantite, 0);
      const fromCode = resolveLocations(prevStore, spaces)[0]?.code || 'non rangé';
      const toLbl = primary?.label || 'non rangé';
      const msg = over.length
        ? `${over[0].code} en surcharge (+${over[0].over} btl)`
        : `${moved} btl ${ref.year} : ${fromCode} \u2192 ${toLbl} \u00b7 déplacement enregistré`;
      toast(msg, {
        duration: 6000, className: over.length ? 'text-red-700' : undefined,
        action: {
          label: 'Annuler',
          onClick: async () => {
            try {
              await caveApi.undoLocations(ref.id, { locations: prevStore, group_id: res.group_id });
            } catch { /* hors-ligne : l'annulation se resynchronisera */ }
            qc.invalidateQueries({ queryKey: ['cuvee', cuveeId] });
          },
        },
      });
      qc.invalidateQueries({ queryKey: ['location-spaces'] });
    } catch (e) {
      toast.error(e.message || 'Rangement impossible');
      qc.invalidateQueries({ queryKey: ['cuvee', cuveeId] });
    }
  }, [qc, cuveeId, spaces]);

  return { state, openAt, close, place, isOpen: !!state };
}

/** Grille d'emplacements d'un espace découpé (bouton « Préciser ▾ »). */
function EmplacementGrid({ space, cuveeEmps, currentEmps, onPick }) {
  return (
    <div className="grid gap-1 mt-1.5 mb-1" style={{ gridTemplateColumns: `repeat(${space.columns}, minmax(0, 1fr))` }} data-testid={`grid-${space.id}`}>
      {space.emplacements.map((e) => {
        const fill = space.capacity_per_emp ? Math.min(1, e.occupied / space.capacity_per_emp) : 0;
        const full = e.free <= 0;
        const hasCuvee = cuveeEmps.has(`${space.id}:${e.key}`);
        const isCurrent = currentEmps.has(`${space.id}:${e.key}`);
        return (
          <button key={e.key} onClick={() => onPick({ space_id: space.id, emp: e.key })} title={`${e.code} · ${freeLabel(e.free)}`}
            data-testid={`emp-${e.code}`}
            className={`relative h-9 rounded text-[11px] nums flex items-center justify-center transition-colors
              ${isCurrent ? 'ring-2 ring-gold' : 'border border-hair'} ${full ? 'vd-hatch text-n500' : 'hover:bg-gold100'}`}
            style={!full ? { backgroundColor: `rgba(182,130,53,${0.08 + fill * 0.42})` } : undefined}>
            {e.index}
            {hasCuvee && <span className="absolute top-0.5 right-0.5 w-1.5 h-1.5 rounded-full bg-gold" />}
          </button>
        );
      })}
    </div>
  );
}

/** Le popover lui-même (présentation). Rendu via portal, positionné sur l'ancre. */
export function LocationPicker({ picker, references = [], readOnly }) {
  const { state, close, place } = picker;
  const { data } = useQuery({ queryKey: ['location-spaces'], queryFn: caveApi.locationsData, enabled: !!state });
  const spaces = data?.spaces || [];
  const [q, setQ] = useState('');
  const [expanded, setExpanded] = useState(null); // space_id déplié (grille)
  const [distribute, setDistribute] = useState(false);
  const [dist, setDist] = useState([]); // répartition en cours [{space_id, emp, quantite}]
  const inputRef = useRef(null);
  const popRef = useRef(null);

  const ref = useMemo(() => references.find((r) => r.id === state?.ref?.id) || state?.ref, [references, state]);
  const qtyTotal = ref?.quantity || 0;
  const current = useMemo(() => resolveLocations(toStore(ref?.locations), spaces), [ref, spaces]);
  const rangedSum = current.reduce((s, l) => s + l.quantite, 0);
  const unranged = qtyTotal - rangedSum;

  // Emplacements occupés par la cuvée (point accent) et par CETTE référence (contour accent).
  const cuveeEmps = useMemo(() => {
    const set = new Set();
    references.forEach((r) => (r.locations || []).forEach((l) => { if (l.emp) set.add(`${l.space_id}:${l.emp}`); }));
    return set;
  }, [references]);
  const currentEmps = useMemo(() => new Set(current.filter((l) => l.emp).map((l) => `${l.space_id}:${l.emp}`)), [current]);

  useEffect(() => { if (state) { setQ(''); setExpanded(null); setDistribute(false); setDist([]); setTimeout(() => inputRef.current?.focus(), 30); } }, [state]);

  // Fermeture : clic extérieur + Échap.
  useEffect(() => {
    if (!state) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    const onClick = (e) => { if (popRef.current && !popRef.current.contains(e.target)) close(); };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClick);
    return () => { document.removeEventListener('keydown', onKey); document.removeEventListener('mousedown', onClick); };
  }, [state, close]);

  if (!state) return null;

  // Positionnement : sous l'ancre, sinon au-dessus si moins de 420 px en dessous.
  const W = 380, gap = 6, vh = window.innerHeight, vw = window.innerWidth;
  const a = state.anchorRect;
  const spaceBelow = vh - a.bottom;
  const openAbove = spaceBelow < 420 && a.top > spaceBelow;
  let left = state.align === 'right' ? a.right - W : a.left;
  left = Math.max(8, Math.min(left, vw - W - 8));
  const posStyle = openAbove
    ? { bottom: vh - a.top + gap, left, maxHeight: a.top - 16 }
    : { top: a.bottom + gap, left, maxHeight: spaceBelow - 16 };

  // Candidats de recherche : niveau espace + emplacements précis.
  const candidates = [];
  spaces.forEach((s) => {
    candidates.push({ type: 'space', space: s, code: s.short_code, name: s.name, free: s.free, ncode: norm(s.short_code), nname: norm(s.name) });
    if (!s.single) s.emplacements.forEach((e) => candidates.push({ type: 'emp', space: s, emp: e, code: e.code, name: s.name, free: e.free, ncode: norm(e.code), nname: norm(s.name) }));
  });
  const nq = norm(q);
  const results = nq
    ? candidates.filter((c) => c.ncode.includes(nq) || c.nname.includes(nq)).sort((x, y) => {
      const xr = (x.ncode === nq ? 0 : x.ncode.startsWith(nq) ? 1 : 2) + (x.type === 'space' ? 0 : 0.5);
      const yr = (y.ncode === nq ? 0 : y.ncode.startsWith(nq) ? 1 : 2) + (y.type === 'space' ? 0 : 0.5);
      return xr - yr;
    }).slice(0, 40)
    : [];

  // Raccourci : rangement d'un autre millésime de la même cuvée (s'il diffère).
  const sibling = references.find((r) => r.id !== ref.id && (r.locations || []).length);
  const siblingPrimary = sibling?.locations?.[0];
  const showSibling = siblingPrimary && siblingPrimary.code !== current[0]?.code;

  // --- Placement en un clic ---
  const applyClick = (target) => {
    if (readOnly) return;
    let next;
    if (unranged > 0) {
      next = toStore(current);
      const hit = next.find((l) => l.space_id === target.space_id && (l.emp || null) === (target.emp || null));
      if (hit) hit.quantite += unranged; else next.push({ ...target, emp: target.emp || null, quantite: unranged });
    } else {
      next = [{ space_id: target.space_id, emp: target.emp || null, quantite: qtyTotal }];
    }
    place(ref, next);
  };
  const removeLocation = (loc) => {
    const next = toStore(current).filter((l) => !(l.space_id === loc.space_id && (l.emp || null) === (loc.emp || null)));
    place(ref, next, { keepOpen: true });
  };

  // --- Répartir sur plusieurs ---
  const startDistribute = () => { setDistribute(true); setDist(toStore(current)); };
  const addToDist = (target) => {
    setDist((d) => (d.some((l) => l.space_id === target.space_id && (l.emp || null) === (target.emp || null)) ? d
      : [...d, { space_id: target.space_id, emp: target.emp || null, quantite: 0 }]));
  };
  const distTotal = dist.reduce((s, l) => s + l.quantite, 0);
  const applyDistribute = () => { place(ref, dist.filter((l) => l.quantite > 0)); };

  const resolvedDist = resolveLocations(dist, spaces);

  return createPortal(
    <div ref={popRef} data-testid="location-picker" role="dialog" aria-label="Choisir un emplacement"
      className="fixed z-[60] bg-paper border border-gold rounded-lg shadow-lg flex flex-col animate-fade-in"
      style={{ width: W, ...posStyle }}>
      {/* En-tête */}
      <div className="flex items-center justify-between px-3.5 h-11 hair-b shrink-0">
        <span className="text-[13px]"><span className="font-title text-base">{state.vintage?.year || ref.year}</span> <span className="text-n500">{SEP} {ref.format_label}</span></span>
        <span className="flex items-center gap-2">
          <span className="text-[12px] text-n600 nums">{qtyTotal} btl</span>
          <button onClick={close} data-testid="lp-close" className="text-n500 hover:text-ink"><X className="w-4 h-4" /></button>
        </span>
      </div>

      <div className="overflow-auto px-3.5 py-2.5 flex-1">
        {/* Répartition en cours */}
        {distribute && (
          <div className="vd-card p-2 mb-2.5" data-testid="distribute-box">
            <p className="kicker mb-1.5">Répartir sur plusieurs</p>
            {resolvedDist.length === 0 && <p className="text-[12px] text-n500 mb-1">Cliquez des emplacements ci-dessous pour les ajouter.</p>}
            {resolvedDist.map((l, i) => (
              <div key={`${l.space_id}:${l.emp || ''}`} className="flex items-center gap-2 py-1 text-[13px]">
                <span className="flex-1 min-w-0 break-words">{l.label}</span>
                <span className="flex items-center gap-1">
                  <button className="w-6 h-6 rounded border border-hair hover:bg-surface" data-testid={`dist-minus-${i}`}
                    onClick={() => setDist((d) => d.map((x, j) => j === i ? { ...x, quantite: Math.max(0, x.quantite - 1) } : x))}>−</button>
                  <span className="w-7 text-center nums">{l.quantite}</span>
                  <button className="w-6 h-6 rounded border border-hair hover:bg-surface" data-testid={`dist-plus-${i}`}
                    onClick={() => setDist((d) => d.map((x, j) => j === i ? { ...x, quantite: x.quantite + 1 } : x))}>+</button>
                  <button className="ml-1 text-n500 hover:text-red-700" data-testid={`dist-remove-${i}`}
                    onClick={() => setDist((d) => d.filter((_, j) => j !== i))}><X className="w-3.5 h-3.5" /></button>
                </span>
              </div>
            ))}
            <div className="flex items-center justify-between mt-1.5 hair-t pt-1.5">
              <span className={`text-[13px] nums ${distTotal === qtyTotal ? 'text-green-700' : 'text-red-700'}`} data-testid="dist-counter">{distTotal} / {qtyTotal}{distTotal === qtyTotal ? ' \u2713' : ''}</span>
              <span className="flex gap-1.5">
                <button className="vd-btn vd-btn-ghost h-7 text-[12px]" onClick={() => setDistribute(false)} data-testid="dist-cancel">Annuler</button>
                <button className="vd-btn vd-btn-primary h-7 text-[12px]" disabled={distTotal !== qtyTotal} onClick={applyDistribute} data-testid="dist-apply">Appliquer</button>
              </span>
            </div>
          </div>
        )}

        {/* Actuellement */}
        {!distribute && current.length > 0 && (
          <div className="mb-2" data-testid="lp-current">
            <p className="kicker mb-1">Actuellement</p>
            {current.map((l) => (
              <div key={`${l.space_id}:${l.emp || ''}`} className="flex items-center gap-2 py-0.5 text-[13px]" data-testid={`current-${l.code}`}>
                <span className="flex-1 min-w-0 break-words">{l.label}{!l.precise && !l.single ? <span className="text-n500"> {SEP} sans préciser</span> : ''}</span>
                <span className="nums text-n600">{l.quantite} btl</span>
                {!readOnly && <button onClick={() => removeLocation(l)} className="text-n500 hover:text-red-700" data-testid={`current-remove-${l.code}`}><X className="w-3.5 h-3.5" /></button>}
              </div>
            ))}
          </div>
        )}
        {!distribute && unranged > 0 && (
          <p className="text-[12.5px] text-red-700 mb-2" data-testid="lp-unranged">{unranged} btl non rangée{unranged > 1 ? 's' : ''} {SEP} choisissez un emplacement ci-dessous</p>
        )}

        {/* Recherche */}
        {!readOnly && (
          <input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)} data-testid="lp-search"
            onKeyDown={(e) => { if (e.key === 'Enter' && results[0]) { e.preventDefault(); const c = results[0]; (distribute ? addToDist : applyClick)(c.type === 'emp' ? { space_id: c.space.id, emp: c.emp.key } : { space_id: c.space.id, emp: null }); } }}
            placeholder="Taper A, A15, CN4, frais, bar…" className="vd-input h-8 w-full text-[13px] mb-2" />
        )}

        {/* Résultats de recherche */}
        {nq && (
          results.length ? (
            <div className="space-y-0.5" data-testid="lp-results">
              {results.map((c, i) => (
                <button key={`${c.space.id}:${c.emp?.key || 'space'}`} data-testid={`result-${c.code}`}
                  onClick={() => (distribute ? addToDist : applyClick)(c.type === 'emp' ? { space_id: c.space.id, emp: c.emp.key } : { space_id: c.space.id, emp: null })}
                  className={`w-full text-left px-2 h-9 rounded flex items-center gap-2 hover:bg-surface ${i === 0 ? 'bg-gold100/60' : ''}`}>
                  <span className="font-medium">{c.code}</span>
                  <span className="text-[12px] text-n600 flex-1 min-w-0 break-words">{c.name}{c.type === 'space' && !c.space.single ? ` ${SEP} sans préciser` : ''}</span>
                  <span className={`text-[11px] nums shrink-0 ${freeClass(c.free)}`}>{freeLabel(c.free)}</span>
                </button>
              ))}
            </div>
          ) : <p className="text-[12.5px] text-n500 py-2" data-testid="lp-noresult">Aucun emplacement. Vérifiez le code ou créez l'espace dans Paramètres.</p>
        )}

        {/* Raccourci + liste des espaces (champ vide) */}
        {!nq && (
          <div>
            {showSibling && !distribute && (
              <button className="w-full text-left px-2 h-9 rounded flex items-center gap-2 hover:bg-surface mb-1" data-testid="lp-sibling"
                onClick={() => applyClick({ space_id: siblingPrimary.space_id, emp: siblingPrimary.emp || null })}>
                <span className="font-medium">{siblingPrimary.code}</span>
                <span className="text-[12px] text-n600">avec le {sibling.year}</span>
              </button>
            )}
            <div className="space-y-0.5" data-testid="lp-spaces">
              {spaces.map((s) => (
                <div key={s.id}>
                  <div className="flex items-center gap-2 px-2 h-9 rounded hover:bg-surface">
                    <button className="flex items-center gap-2 flex-1 min-w-0 text-left" data-testid={`space-row-${s.short_code}`}
                      onClick={() => (distribute ? addToDist : applyClick)({ space_id: s.id, emp: null })}>
                      <span className="font-medium">{s.short_code}</span>
                      <span className="text-[12px] text-n600 flex-1 min-w-0 break-words">{s.name}</span>
                      <span className={`text-[11px] nums shrink-0 ${freeClass(s.free)}`}>{freeLabel(s.free)}</span>
                    </button>
                    {!s.single && (
                      <button className="text-[11px] text-gold700 hover:underline flex items-center gap-0.5 shrink-0" data-testid={`precise-${s.short_code}`}
                        onClick={() => setExpanded((x) => (x === s.id ? null : s.id))}>
                        Préciser <ChevronDown className={`w-3 h-3 transition-transform ${expanded === s.id ? 'rotate-180' : ''}`} />
                      </button>
                    )}
                  </div>
                  {expanded === s.id && <EmplacementGrid space={s} cuveeEmps={cuveeEmps} currentEmps={currentEmps} onPick={(t) => (distribute ? addToDist : applyClick)(t)} />}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Pied */}
      {!readOnly && !distribute && (
        <div className="flex items-center justify-between px-3.5 h-9 hair-t shrink-0 text-[11px] text-n500">
          <span>↵ place le 1er résultat {SEP} Échap ferme</span>
          <button className="text-gold700 hover:underline" onClick={startDistribute} data-testid="lp-distribute">Répartir sur plusieurs</button>
        </div>
      )}
    </div>,
    document.body,
  );
}
